import jsPDF from "jspdf";
import "jspdf-autotable";
import * as XLSX from "xlsx";

// Extend jsPDF to include autoTable
interface jsPDFWithAutoTable extends jsPDF {
    autoTable: (options: {
        head: (string | number | boolean)[][];
        body: (string | number | boolean)[][];
    }) => void;
}

type PDFExportOptions<T = any> = {
    data: T[];             // Array of objects or rows
    headers: string[];     // Column headers to show in the PDF
    keys: string[];        // Keys to extract values from each object (match headers order)
    fileName: string;      // Output PDF file name (without extension)
};

type ExcelExportOptions<T = any> = {
    data: T[];             // Array of objects or rows
    headers: string[];     // Column headers to show in Excel
    keys: string[];        // Keys to extract values from each object
    fileName: string;      // Output Excel file name (without extension)
};

/**
 * Generates a PDF table from an array of objects.
 * @param data - The array of data objects.
 * @param headers - The column headers for the PDF.
 * @param keys - The object keys to extract values from.
 * @param fileName - The name of the resulting PDF file.
 */
export function exportToPDF({
    data,
    headers,
    keys,
    fileName,
}: PDFExportOptions): void {
    const doc = new jsPDF() as jsPDFWithAutoTable;

    const rows = data.map((item) =>
        keys.map((key) => item?.[key] ?? "")
    );

    doc.autoTable({
        head: [headers],
        body: rows,
    });

    doc.save(`${fileName}.pdf`);
}

/**
 * Example:
 * 
 * exportToPDF({
 *   data: [
 *     { company_name: "Acme Corp", status: "Active" },
 *     { company_name: "Globex", status: "Inactive" },
 *   ],
 *   headers: ["Company Name", "Status"],
 *   keys: ["company_name", "status"],
 *   fileName: "companies",
 * });
 */

/**
 * Exports data to an Excel (.xlsx) file from an array of objects.
 * @param data - The array of data objects.
 * @param headers - The column headers for the Excel sheet.
 * @param keys - The object keys to extract values from.
 * @param fileName - The name of the resulting Excel file.
 */
export function exportToExcel({
    data,
    headers,
    keys,
    fileName,
}: ExcelExportOptions): void {
    const rows = data.map(item =>
        keys.map(key => item?.[key] ?? "")
    );

    const sheetData = [headers, ...rows];
    const worksheet = XLSX.utils.aoa_to_sheet(sheetData);
    const workbook = XLSX.utils.book_new();

    XLSX.utils.book_append_sheet(workbook, worksheet, "Sheet1");
    XLSX.writeFile(workbook, `${fileName}.xlsx`);
}

/**
 * Example:
 * 
 * exportToExcel({
 *   data: [
 *     { company_name: "Acme Corp", status: "Active" },
 *     { company_name: "Globex", status: "Inactive" },
 *   ],
 *   headers: ["Company Name", "Status"],
 *   keys: ["company_name", "status"],
 *   fileName: "companies",
 * });
 */
